'use strict';

/**
 * Adapter: moment for DateTimeHelper
 *
 * NOTE: When making changes to this file you must create an ES5 version prior to release.
 *
 * @version 12.4.0 #756 MLIBJS-121 Add validation to export DateTimeHelperMomentAdapter for ES6 usage
 *          7.17.0 #564 Update `getDateAsString` to take an optional date format
 *          7.16.0 #557 Add missing `wdDateTimeHelper` functionality
 *          7.10.0 #522 Add time conversion functionality
 *
 * @author Dallas Vogels <dvogels@webduct.com>
 * @author Erik Orozco <eorozco@intersog.com>
 *
 * @copyright (c) 2017-2020 Build Centrix
 */
export default class DateTimeHelperMomentAdapter {

    /**
     * Constructor
     *
     * Note that any functions in the constructor are considered private.
     * @see http://stackoverflow.com/a/28165599/1304537
     *
     * @since 7.10.0
     *
     * @param {moment} moment
     * @param {HumanizeDurationMomentAdapter} humanizeAdapter
     */
    constructor(moment, humanizeAdapter) {

        this.format = 'YYYY-MM-DD'; // default

        this.moment = moment;

        this.humanizeAdapter = humanizeAdapter;

        /**
         * Get Date as Native object
         *
         * @since 7.10.0
         *
         * @param {Date|String} dateToFormat
         * @returns {moment}
         */
        this.getDateAsMoment = function (dateToFormat) {
            return dateToFormat ? this.moment(dateToFormat) : null;
        }
    }

    /**
     * Format String Date
     *
     * @since 7.10.0
     *
     * @param {String} dateToFormat
     *
     * @returns {Date|null}
     */
    formatDate(dateToFormat) {

        var momentDateToFormat = this.getDateAsMoment(dateToFormat);
        return momentDateToFormat ? momentDateToFormat.toDate() : null;

    };

    /**
     * Add Days (+/-) to Date
     * @since 7.10.0
     *
     * @param {Date} dateToFormat
     * @param {String} key
     * @param {Number} value
     * @returns {Date|null}
     */
    addTime(dateToFormat, key, value) {

        return this.getDateAsMoment(dateToFormat).add(value, key).toDate();

    };

    /**
     * Get Date as String
     *
     * @since 7.17.0
     *
     * @param {Date} dateToFormat
     * @param {String} format
     * @returns {String|null}
     */
    getDateAsString(dateToFormat, format = null) {

        var stringDateToFormat = this.moment(dateToFormat).format(format ? format : this.getDateFormat());
        return stringDateToFormat ? stringDateToFormat : null;

    };

    /**
     * Get Date Format
     *
     * @since 7.10.0
     *
     * @returns {string}
     */
    getDateFormat() {
        return this.format;
    };

    /**
     * Get Duration Between Dates as Number of Days
     *
     * @since 7.10.0
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @returns {Number}
     */
    getDurationAsDays(startDate, endDate) {

        var momentStartDate = this.getDateAsMoment(startDate);
        var momentEndDate = this.getDateAsMoment(endDate);

        var duration = this.moment.duration(momentEndDate.diff(momentStartDate));
        duration = parseInt(duration.asDays());
        duration = (duration < 0) ? 0 : duration + 1;

        return duration;

    };

    /**
     * Get Start of Current Month
     *
     * @since 7.16.0
     *
     * @param {Date} date
     * @returns {Date}
     */
    getEndOfMonth(date) {

        var selectedDate = date ? date : this.getToday();
        return this.getDateAsMoment(selectedDate).endOf('month').toDate();

    };

    /**
     * Get Start of Current Month
     *
     * @since 7.16.0
     *
     * @param {Date} date
     * @returns {Date}
     */
    getStartOfMonth(date) {

        var selectedDate = date ? date : this.getToday();
        return this.getDateAsMoment(selectedDate).startOf('month').toDate();

    };

    /**
     * Get Time From Now
     *
     * @since 7.16.0
     *
     * @param {Date} date
     * @returns {String}
     */
    getTimeFromNow(date) {
        return this.getDateAsMoment(date).fromNow();
    };

    /**
     * Get Todays Date
     *
     * @since 7.10.0
     *
     * @returns {Date}
     */
    getToday() {

        return this.moment().toDate();

    };

    /**
     * Convert input to milliseconds
     *
     * @since 7.10.0
     *
     * @param {Number} input
     * @param {String} inputUnit
     * @returns {Number}
     */
    convertInputToMilliseconds(input, inputUnit) {
        let result = moment.duration(input, inputUnit);

        result = result.asMilliseconds();

        return result;
    }

    /**
     * Humanize Duration
     *
     * @since 7.10.0
     *
     * @param {Number} input seconds
     * @param {String} inputUnit can be one: 'y', 'M', 'w', 'd', 'h', 'm', 's', 'ms'
     * @param {Array|String} outputUnits e.g. ['y', 'M', 'w', 'd', 'h', 'm', 's', 'ms'], 'm'
     * @param {Boolean} round default false
     *
     * @returns {String}
     */
    humanizeDuration(input, inputUnit, outputUnits, round = false) {

        let inputInMilliseconds = this.convertInputToMilliseconds(input, inputUnit);

        return this.humanizeAdapter.humanizeDuration(inputInMilliseconds, outputUnits, round);
    }

    /**
     * Check if target date is before the match date
     *
     * @since 7.16.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isBefore(dateToCheck, dateToMatch) {

        let dateToCheckHere = this.getDateAsMoment(dateToCheck);
        let dateToMatchHere = this.getDateAsMoment(dateToMatch);

        return dateToCheckHere.isBefore(dateToMatchHere);

    };

    /**
     * Check if target date is after the match date
     *
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isAfter(dateToCheck, dateToMatch) {

        let dateToCheckHere = this.getDateAsMoment(dateToCheck);
        let dateToMatchHere = this.getDateAsMoment(dateToMatch);

        return dateToCheckHere.isAfter(dateToMatchHere);

    };

    /**
     * Is the Target Date within the Date Range?
     *
     * @since 7.10.0
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @param {Date} targetDate
     * @returns {Boolean}
     */
    isDateWithinDateRange(startDate, endDate, targetDate) {

        let result = false;

        if (startDate === null && endDate === null) {

            result = true;

        } else if (startDate === null || endDate === null) {

            if (startDate === null && this.isSameOrBefore(targetDate, endDate)) {

                result = true;

            }
            else if (endDate === null && this.isSameOrAfter(targetDate, startDate)) {

                result = true;

            }

        } else {

            // start date must be before end date; this is checked where a tracker with no end date
            // is set to be the current date; can happen

            // ensure tracker didn't end before the report start date
            if (this.isSameOrAfter(targetDate, startDate)) {

                // ensure tracker started before the report end date
                if (this.isSameOrBefore(targetDate, endDate)) {
                    result = true;
                }
            }

        }

        return result;

    };

    /**
     * Are Two Dates The Same?
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isSameDate(dateToCheck, dateToMatch) {

        let momentDateToCheck = this.getDateAsMoment(dateToCheck).startOf('day');
        let momentDateToMatch = this.getDateAsMoment(dateToMatch).startOf('day');

        return momentDateToCheck.isSame(momentDateToMatch);

    };

    /**
     * Check if target date is the same or after the match date
     *
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isSameOrAfter(dateToCheck, dateToMatch) {

        let momentDateToCheck = this.getDateAsMoment(dateToCheck);
        let momentDateToMatch = this.getDateAsMoment(dateToMatch);

        return momentDateToCheck.isSameOrAfter(momentDateToMatch);

    };

    /**
     * Check if target date is the same or before the match date
     *
     * @since 7.10.0
     *
     * @param {Date} dateToCheck
     * @param {Date} dateToMatch
     * @returns {Boolean}
     */
    isSameOrBefore(dateToCheck, dateToMatch) {

        let momentDateToCheck = this.getDateAsMoment(dateToCheck);
        let momentDateToMatch = this.getDateAsMoment(dateToMatch);

        return momentDateToCheck.isSameOrBefore(momentDateToMatch);

    };

    /**
     * Set Date Format
     *
     * @since 7.10.0
     *
     * @param {string} format
     */
    setDateFormat(format) {
        this.format = format;
    };

    /**
     * Given two date ranges check if date range #1 contains date range #2.
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted Date started of container range
     * @param {Date} dateEnded Date ended of container range
     * @param {Date} targetDateStarted Date started of contained range
     * @param {Date} targetDateEnded Date ended of contained range
     *
     * @return {Boolean}
     */
    containsDateDuration(dateStarted, dateEnded, targetDateStarted, targetDateEnded) {

        let isTargetDateStartedInDateRange = this.isDateWithinDateRange(dateStarted, dateEnded, targetDateStarted);
        let isTargetDateEndedInDateRange = this.isDateWithinDateRange(dateStarted, dateEnded, targetDateEnded);

        let result = isTargetDateStartedInDateRange && isTargetDateEndedInDateRange;

        return result;
    }

    /**
     * Check if a start date intersects a date range.
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted The start date to check
     * @param {Date} targetDateStarted The start date of the target date range
     * @param {Date} targetDateEnded The end date of the target date range
     * @param {Boolean} ignoreExactMatch If true and dateStarted is the same as the targetDateEnded then result returned is false (i.e no intersect)
     *
     * @returns {Boolean}
     */
    startDateIntersects(dateStarted, targetDateStarted, targetDateEnded, ignoreExactMatch = false) {
        let result = false;

        if (ignoreExactMatch && this.moment(dateStarted).isSame(targetDateEnded)) {
            result = false;
        } else {
            result = this.dateIntersects(dateStarted, targetDateStarted, targetDateEnded);
        }

        return result;
    }

    /**
     * Check if an end date intersects a date range.
     *
     * @since 7.10.0
     *
     * @param {Date} dateEnded The end date to check
     * @param {Date} targetDateStarted The start date of the target date range
     * @param {Date} targetDateEnded The end date of the target date range
     * @param {Boolean} ignoreExactMatch If true and dateEnded is the same as the targetDateStarted then result returned is false (i.e no intersect)
     *
     * @returns {Boolean}
     */
    endDateIntersects(dateEnded, targetDateStarted, targetDateEnded, ignoreExactMatch = false) {

        let result = false;

        if (ignoreExactMatch && this.moment(dateEnded).isSame(targetDateStarted)) {
            result = false;
        } else {
            result = this.dateIntersects(dateEnded, targetDateStarted, targetDateEnded);
        }

        return result;

    }

    /**
     * Check if a date intersects a date range
     *
     * @since 7.10.0
     *
     * @param {Date} intersectDate Date to check for intersection
     * @param {Date} compareDateStarted Start date of date range to check
     * @param {Date} compareDateEnded End date of date range to check
     *
     * @returns {Boolean}
     */
    dateIntersects(intersectDate, compareDateStarted, compareDateEnded) {
        return this.isDateWithinDateRange(compareDateStarted, compareDateEnded, intersectDate);
    }

    /**
     * Check if a date range (targetDateStarted, targetDateEnded) envelops another date range (dateStarted, dateEnded)
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted The start date of the range to check if it is enveloped by the target range
     * @param {Date} dateEnded The end date of the range to check if it is enveloped by the target range
     * @param {Date} targetDateStarted The start date of the range to check if it envelopes
     * @param {Date} targetDateEnded The end date of the range to check if it envelopes
     *
     * @returns {Boolean}
     */
    envelops(dateStarted, dateEnded, targetDateStarted, targetDateEnded) {
        return this.containsDateDuration(targetDateStarted, targetDateEnded, dateStarted, dateEnded);
    }

    /**
     * Check if a date range (targetDateStarted, targetDateEnded) is enveloped by another date range (dateStarted, dateEnded)
     *
     * @since 7.10.0
     *
     * @param {Date} dateStarted The start date of the range to check if it envelops the target range
     * @param {Date} dateEnded The end date of the range to check if it envelops the target range
     * @param {Date} targetDateStarted The start date of the range to check if it is being enveloped
     * @param {Date} targetDateEnded The end date of the range to check if it is being enveloped
     *
     * @returns {Boolean}
     */
    enveloped(dateStarted, dateEnded, targetDateStarted, targetDateEnded) {
        return this.containsDateDuration(dateStarted, dateEnded, targetDateStarted, targetDateEnded);
    }

}

// CommonJS: this must be after the class declaration
if (typeof module !== 'undefined' && module.exports) {
    module.exports = DateTimeHelperMomentAdapter;
}

// EOF